import * as React from 'react';
import { AppUser } from 'Src/views/App/App';
import { getEventCommunicationConfig, getEventConversations } from 'Src/helpers/service/eventCommunications';
import { EventCommunicationConfig } from 'Src/models/eventCommunication';
import './SchoolEventGroupedTabs.scss';

interface Props {
  tabs: string[];
  currentTabIndex: number;
  onTabClick: (event: React.MouseEvent<any>, index: number) => void;
  children?: React.ReactNode;
  portalRole?: 'parent' | 'student';
  communicationsUser?: AppUser;
  communicationsEventId?: string;
  groupBadges?: Record<string, number>;
}

interface TabDefinition {
  label: string;
  displayLabel?: string;
  icon: string;
}

interface TabGroup {
  label: string;
  tabs: TabDefinition[];
}

const ICONS = {
  info: 'M12 22a10 10 0 1 0 0-20 10 10 0 0 0 0 20M12 16v-4M12 8h.01',
  users:
    'M17 21v-2a4 4 0 0 0-4-4H5a4 4 0 0 0-4 4v2M9 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8M23 21v-2a4 4 0 0 0-3-3.87M16 3.13a4 4 0 0 1 0 7.75',
  grid: 'M3 3h7v7H3zM14 3h7v7h-7zM14 14h7v7h-7zM3 14h7v7H3z',
  clipboard:
    'M9 2h6a1 1 0 0 1 1 1v2H8V3a1 1 0 0 1 1-1zM8 5H6a2 2 0 0 0-2 2v13a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V7a2 2 0 0 0-2-2h-2',
  whistle: 'M12 3l1.9 5.1L19 10l-5.1 1.9L12 17l-1.9-5.1L5 10l5.1-1.9z',
  star: 'M12 3l2.9 6.3 6.9.7-5.1 4.6 1.4 6.8L12 18.8 5.9 21.4l1.4-6.8L2.2 10l6.9-.7z',
  flag: 'M4 15s1-1 4-1 5 2 8 2 4-1 4-1V3s-1 1-4 1-5-2-8-2-4 1-4 1zM4 22v-7',
  award: 'M12 15a6 6 0 1 0 0-12 6 6 0 0 0 0 12M8.2 13.5 7 22l5-3 5 3-1.2-8.5',
  shirt: 'M7 3l-4 4 3 3 1-1v12h10V9l1 1 3-3-4-4h-3a2 2 0 0 1-4 0z',
  image:
    'M4 4h16a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V6a2 2 0 0 1 2-2M8.5 11a1.5 1.5 0 1 0 0-3 1.5 1.5 0 0 0 0 3M21 15l-5-5L5 21',
  message: 'M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z',
  settings: 'M12 15.5a3.5 3.5 0 1 0 0-7 3.5 3.5 0 0 0 0 7M19.4 15a1.7 1.7 0 0 0 .34 1.88l.06.06-2.12 2.12-.06-.06a1.7 1.7 0 0 0-1.88-.34 1.7 1.7 0 0 0-1.02 1.56V20h-3v-.08a1.7 1.7 0 0 0-1.02-1.56 1.7 1.7 0 0 0-1.88.34l-.06.06-2.12-2.12.06-.06A1.7 1.7 0 0 0 7 14.7a1.7 1.7 0 0 0-1.56-1.02H5v-3h.08A1.7 1.7 0 0 0 6.64 9.7a1.7 1.7 0 0 0-.34-1.88l-.06-.06 2.12-2.12.06.06A1.7 1.7 0 0 0 10.3 6a1.7 1.7 0 0 0 1.02-1.56V4h3v.08A1.7 1.7 0 0 0 15.3 5.64a1.7 1.7 0 0 0 1.88-.34l.06-.06 2.12 2.12-.06.06A1.7 1.7 0 0 0 19 9.3a1.7 1.7 0 0 0 1.56 1.02H21v3h-.08A1.7 1.7 0 0 0 19.4 15z',
  user: 'M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2M12 11a4 4 0 1 0 0-8 4 4 0 0 0 0 8',
  shield: 'M12 22s8-4 8-10V5l-8-3-8 3v7c0 6 8 10 8 10z',
  doc: 'M14 2H6a2 2 0 0 0-2 2v16a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2V8zM14 2v6h6M9 13h6M9 17h4'
};

const GROUPS: TabGroup[] = [
  {
    label: 'Overview',
    tabs: [
      { label: 'Participants', icon: 'users' },
      { label: 'Teams', icon: 'users' },
      { label: 'Team members', icon: 'users' },
      { label: 'Players', icon: 'users' },
      { label: 'Details', icon: 'info' },
      { label: 'Positions', icon: 'grid' }
    ]
  },
  {
    label: 'On the day',
    tabs: [
      { label: 'Attendance', icon: 'clipboard' },
      { label: 'Match report', icon: 'whistle' },
      { label: 'Leader/Coach notes', icon: 'whistle' },
      { label: 'Performance', icon: 'star' },
      { label: 'Progress', icon: 'star' },
      { label: 'Discipline', icon: 'flag' },
      { label: 'Jobs', icon: 'user' }
    ]
  },
  {
    label: 'Recognition',
    tabs: [
      { label: 'Awards', icon: 'award' },
      { label: 'Kit', icon: 'shirt' }
    ]
  },
  {
    label: 'Media',
    tabs: [{ label: 'Gallery', icon: 'image' }]
  },
  {
    label: 'Chats',
    tabs: [
      { label: 'Communications', displayLabel: 'Chats', icon: 'message' },
      { label: 'Communication notifications', displayLabel: 'Notification settings', icon: 'settings' }
    ]
  },
  {
    label: 'Consent & Availability',
    tabs: [
      { label: 'Parental consent', icon: 'shield' },
      { label: 'Availability reports', icon: 'doc' }
    ]
  }
];

const FALLBACK_GROUP: TabGroup = { label: 'More', tabs: [] };

const TabIcon = ({ name }: { name: string }) => (
  <svg
    viewBox="0 0 24 24"
    width="16"
    height="16"
    fill="none"
    stroke="currentColor"
    strokeWidth="1.9"
    strokeLinecap="round"
    strokeLinejoin="round"
    aria-hidden="true"
  >
    <path d={ICONS[name] || ICONS.info} />
  </svg>
);

export const SchoolEventGroupedTabs: React.FC<Props> = ({
  tabs,
  currentTabIndex,
  onTabClick,
  children,
  portalRole,
  communicationsUser,
  communicationsEventId,
  groupBadges = {}
}) => {
  const [communicationsUnread, setCommunicationsUnread] = React.useState(0);
  const [communicationsConfig, setCommunicationsConfig] = React.useState<EventCommunicationConfig | undefined>();
  const communicationsAvailable =
    !communicationsUser || !communicationsUser.activeSchool ||
    (communicationsUser.activeSchool as any).eventCommunicationsAvailable !== false;

  React.useEffect(() => {
    if (!communicationsUser || !communicationsEventId || !communicationsAvailable) return undefined;
    let active = true;
    const loadUnread = async () => {
      try {
        const config = await getEventCommunicationConfig(communicationsUser, communicationsEventId);
        if (!active) return;
        setCommunicationsConfig(config);
        const hasRoleAccess = config.eventEnabled !== false && (config.publicChatEnabled || config.privateMessagesAccessible || config.privateMessagesEnabled);
        if (portalRole && !hasRoleAccess) {
          setCommunicationsUnread(0);
          return;
        }
        const items = await getEventConversations(communicationsUser, communicationsEventId);
        if (active) setCommunicationsUnread(items.reduce((sum, item) => sum + Number(item.unreadCount || 0), 0));
      } catch (error) {
        return undefined;
      }
    };
    const onUnreadChanged = (event: Event) => {
      const detail = (event as CustomEvent).detail;
      if (!detail || String(detail.eventId) === String(communicationsEventId)) loadUnread();
    };
    loadUnread();
    const timer = window.setInterval(loadUnread, 30000);
    window.addEventListener('sit:event-communications-unread-changed', onUnreadChanged);
    return () => {
      active = false;
      window.clearInterval(timer);
      window.removeEventListener('sit:event-communications-unread-changed', onUnreadChanged);
    };
  }, [communicationsUser, communicationsEventId, communicationsAvailable, portalRole]);
  const groups = GROUPS.map(group => ({ ...group, tabs: [...group.tabs] }));
  const communicationsRoleAccess = !portalRole || !communicationsConfig || (
    communicationsConfig.eventEnabled !== false &&
    (communicationsConfig.publicChatEnabled || communicationsConfig.privateMessagesAccessible || communicationsConfig.privateMessagesEnabled)
  );
  if (!communicationsAvailable || !communicationsRoleAccess) {
    const chatsGroup = groups.find(group => group.label === 'Chats');
    if (chatsGroup) {
      chatsGroup.tabs = chatsGroup.tabs.filter(tab => tab.label === 'Communications');
    }
  }
  if (portalRole === 'student') {
    const responsesGroup = groups.find(group => group.label === 'Consent & Availability');
    if (responsesGroup) responsesGroup.label = 'Availability';
  }

  const availableGroups = groups
    .map(group => ({
      ...group,
      tabs: group.tabs.filter(tab => tabs.includes(tab.label))
    }))
    .filter(group => group.tabs.length > 0);

  const mappedLabels = ([] as string[]).concat(...availableGroups.map(group => group.tabs.map(tab => tab.label)));
  const unmappedTabs = tabs.filter(tab => !mappedLabels.includes(tab));
  if (unmappedTabs.length > 0) {
    availableGroups.push({
      ...FALLBACK_GROUP,
      tabs: unmappedTabs.map(label => ({ label, icon: 'info' }))
    });
  }

  const currentTab = tabs[currentTabIndex] || tabs[0];
  const currentGroup =
    availableGroups.find(group => group.tabs.some(tab => tab.label === currentTab)) || availableGroups[0];

  const selectTab = (event: React.MouseEvent<any>, label: string) => {
    const index = tabs.indexOf(label);
    if (index >= 0) onTabClick(event, index);
  };

  return (
    <div className="eEventGroupedTabs">
      <nav className="eEventGroupedTabs__nav" aria-label="Event sections">
        <div className="eEventGroupedTabs__groups" role="tablist" aria-label="Event section groups">
          {availableGroups.map(group => {
            const isActive = group.label === currentGroup.label;
            const badgeCount = Number(groupBadges[group.label] || 0) + (group.label === 'Chats' ? communicationsUnread : 0);
            return (
              <button
                key={group.label}
                type="button"
                className={
                  isActive ? 'eEventGroupedTabs__group eEventGroupedTabs__group--active' : 'eEventGroupedTabs__group'
                }
                aria-selected={isActive}
                onClick={event => {
                  const nextTab =
                    isActive && group.tabs.some(tab => tab.label === currentTab) ? currentTab : group.tabs[0].label;
                  selectTab(event, nextTab);
                }}
              >
                {group.label}
                {badgeCount > 0 ? (
                  <span className="eEventGroupedTabs__groupBadge" aria-label={`${badgeCount} requiring attention`}>
                    {badgeCount > 99 ? '99+' : badgeCount}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>

        <div className="eEventGroupedTabs__items" role="tablist" aria-label={currentGroup.label}>
          {currentGroup.tabs.map(tab => {
            const isActive = tab.label === currentTab;
            return (
              <button
                key={tab.label}
                type="button"
                role="tab"
                aria-selected={isActive}
                className={
                  isActive ? 'eEventGroupedTabs__item eEventGroupedTabs__item--active' : 'eEventGroupedTabs__item'
                }
                onClick={event => selectTab(event, tab.label)}
              >
                <span className="eEventGroupedTabs__itemIcon">
                  <TabIcon name={tab.icon} />
                </span>
                {tab.displayLabel || tab.label}
                {tab.label === 'Communications' && communicationsUnread > 0 ? (
                  <span className="eEventGroupedTabs__badge" aria-label={`${communicationsUnread} unread messages`}>
                    {communicationsUnread > 99 ? '99+' : communicationsUnread}
                  </span>
                ) : null}
              </button>
            );
          })}
        </div>
      </nav>

      <div
        className={
          currentTab === 'Positions'
            ? 'eEventGroupedTabs__content eEventGroupedTabs__content--positions'
            : 'eEventGroupedTabs__content'
        }
      >
        {children}
      </div>
    </div>
  );
};
